import { ReactNode, useEffect, useState, FC } from 'react';
import { IntlProvider as ReactIntlProvider } from 'react-intl';

import { DEFAULT_LOCALE, LocalStorageKey } from '@/shared/config';
import { useAppSelector } from '@/shared/hooks';
import { getAvailableLanguages } from '@/shared/lib';

import { userModel } from '@/entities/user';

import { getMessages, isLanguageRTL, normalizeLocale } from '@/features/intl';
import messages_en_US from '@/features/intl/lang/en-US.json';

type IntlProviderProps = {
  children: ReactNode;
};

export const IntlProvider: FC<IntlProviderProps> = (props) => {
  const { children } = props;

  const [messages, setMessages] = useState<Record<string, string>>(
    () => messages_en_US,
  );

  const backendLocale = useAppSelector(userModel.selectors.selectUserLocale);

  const unauthenticatedLocale = localStorage.getItem(
    LocalStorageKey.unauthenticatedLocale,
  );

  const locale = backendLocale ?? unauthenticatedLocale ?? DEFAULT_LOCALE;
  const normalizedLocale = normalizeLocale(locale);

  const availableLanguages = getAvailableLanguages();

  useEffect(() => {
    const init = async () => {
      const msg = await getMessages(normalizedLocale);

      setMessages(msg);
    };

    init();
  }, [normalizedLocale]);

  useEffect(() => {
    if (isLanguageRTL(normalizedLocale, availableLanguages)) {
      document.body.setAttribute('dir', 'rtl');
    } else {
      document.body.setAttribute('dir', 'ltr');
    }
  }, [normalizedLocale, availableLanguages]);

  return (
    <ReactIntlProvider
      defaultLocale="en"
      locale={normalizedLocale}
      messages={messages}
      onError={() => {}}
    >
      {children}
    </ReactIntlProvider>
  );
};
